import { useEffect, useState } from "react";
import { Menu, X, Phone, ChevronDown, MapPin } from "lucide-react";
import logo from "@/assets/logo-200.webp";

const outpatientPrograms: { label: string; href: string }[] = [
  { label: "Partial Hospitalization (PHP)", href: "/php-cherry-hill-nj" },
  { label: "Intensive Outpatient (IOP)", href: "/iop-cherry-hill-nj" },
  { label: "Evening IOP", href: "/evening-iop-new-jersey" },
  { label: "Virtual IOP", href: "/virtual-iop-new-jersey" },
  { label: "Detox Referral", href: "/detox-referral-coordination-nj" },
];

const additionalServices: { label: string; href: string; badge?: string }[] = [
  { label: "Medication Assisted Treatment", href: "/medication-assisted-treatment-nj" },
  { label: "Suboxone & Sublocade", href: "/suboxone-doctor-cherry-hill-nj" },
  { label: "Psychiatric Evaluations", href: "/psychiatric-evaluation-nj" },
  { label: "Drug & Alcohol Evaluations", href: "/drug-alcohol-evaluation-nj" },
  { label: "Recovery Housing", href: "/recovery-housing-nj", badge: "Optional" },
  { label: "Family Programming", href: "/family-guide-new-jersey" },
  { label: "Therapies We Use", href: "/therapies-cherry-hill-nj" },
];

const conditions: { label: string; href: string }[] = [
  { label: "Heroin & Opioid Addiction", href: "/heroin-opioid-addiction-treatment-nj" },
  { label: "Alcohol Addiction", href: "/alcohol-addiction-treatment-cherry-hill-nj" },
  { label: "Drug Rehab (All Substances)", href: "/drug-rehab-cherry-hill-nj" },
  { label: "Fentanyl Addiction", href: "/fentanyl-addiction-treatment-nj" },
  { label: "Cocaine Addiction", href: "/cocaine-addiction-treatment-nj" },
  { label: "Benzodiazepine Addiction", href: "/benzodiazepine-addiction-treatment-nj" },
  { label: "Marijuana Addiction", href: "/marijuana-addiction-treatment-nj" },
  { label: "Painkiller Addiction", href: "/painkiller-addiction-treatment-nj" },
  { label: "Adderall Addiction", href: "/adderall-addiction-treatment-nj" },
  { label: "Methadone Addiction", href: "/methadone-addiction-treatment-nj" },
  { label: "Synthetic Drugs (K2, Zaza)", href: "/synthetic-drug-treatment-nj" },
];

const aboutItems = [
  { label: "Our Story & Team", href: "/about" },
  { label: "Resources", href: "/addiction-resources-new-jersey" },
];

const navLinkClass =
  "relative text-[14px] xl:text-[15px] tracking-[0.02em] text-body-dark hover:text-burgundy transition-colors after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[2px] after:bg-burgundy after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const next = window.scrollY > 20;
        setScrolled((prev) => (prev === next ? prev : next));
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <div className="fixed top-0 inset-x-0 z-50">
      {/* Utility Bar */}
      <div className="bg-crimson-dark text-cream-warm hidden md:block">
        <div className="container flex items-center justify-between h-9 text-[13px]">
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 opacity-80" />
            <span>1930 Marlton Pike East, Cherry Hill, NJ 08003</span>
          </div>
          <a href="tel:8562003127" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Phone className="h-3.5 w-3.5" />
            <span>856-200-3127</span>
          </a>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`bg-white border-b border-warm-border transition-shadow duration-300 ${
          scrolled ? "shadow-[0_2px_12px_rgba(107,20,20,0.08)]" : ""
        }`}
      >
        <div className="container flex items-center justify-between h-[72px]">
          <a href="/" aria-label="Cherry Hill Recovery Center home" className="flex items-center">
            <img src={logo} alt="Cherry Hill Recovery Center logo" width={144} height={37} fetchPriority="high" decoding="async" className="h-[29px] md:h-[37px] w-auto" />
          </a>

          <nav className="hidden min-[1180px]:flex items-center gap-5 xl:gap-8" aria-label="Primary">
            {/* Treatment Programs Mega Menu */}
            <div className="relative group">
              <button className={`inline-flex items-center gap-1 ${navLinkClass}`}>
                Treatment Programs
                <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-[560px]">
                <div className="bg-white border border-warm-border border-t-[3px] border-t-burgundy rounded-b-lg shadow-[0_8px_24px_rgba(107,20,20,0.12)] overflow-hidden">
                  <div className="grid grid-cols-2 gap-0 py-2">
                    <div>
                      <div className="px-5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-burgundy/70">Outpatient Programs</div>
                      {outpatientPrograms.map((item) => (
                        <DropLink key={item.label} href={item.href}>{item.label}</DropLink>
                      ))}
                    </div>
                    <div className="border-l border-warm-border">
                      <div className="px-5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-burgundy/70">Additional Services</div>
                      {additionalServices.map((item) => (
                        <DropLink key={item.label} href={item.href} badge={item.badge}>{item.label}</DropLink>
                      ))}
                    </div>
                  </div>
                  <a href="tel:8562003127" className="block bg-cream-warm text-burgundy italic text-[13px] text-center py-3 hover:bg-cream-warm/70 transition-colors">
                    Not sure which program fits? Call us — 856-200-3127
                  </a>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="relative group">
              <button className={`inline-flex items-center gap-1 ${navLinkClass}`}>
                Conditions We Treat
                <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-72">
                <div className="bg-white border border-warm-border border-t-[3px] border-t-burgundy rounded-b-lg shadow-[0_8px_24px_rgba(107,20,20,0.12)] py-2">
                  {conditions.map((item) => (
                    <DropLink key={item.label} href={item.href}>{item.label}</DropLink>
                  ))}
                </div>
              </div>
            </div>

            <a href="/verify-insurance" className={`${navLinkClass} inline-flex items-center gap-1`}>
              <span style={{ color: "#C9A84C" }}>⚡</span> Insurance
            </a>

            {/* About */}
            <div className="relative group">
              <button className={`inline-flex items-center gap-1 ${navLinkClass}`}>
                About
                <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-56">
                <div className="bg-white border border-warm-border border-t-[3px] border-t-burgundy rounded-b-lg shadow-[0_8px_24px_rgba(107,20,20,0.12)] py-2">
                  {aboutItems.map((item) => (
                    <DropLink key={item.label} href={item.href}>{item.label}</DropLink>
                  ))}
                </div>
              </div>
            </div>

            <a href="/addiction-resources-new-jersey" className={navLinkClass}>Resources</a>
            <a href="/contact" className={navLinkClass}>Contact</a>
          </nav>

          <div className="hidden lg:block">
            <a
              href="tel:8562003127"
              className="inline-flex items-center gap-1 bg-burgundy hover:bg-crimson-dark text-cream-warm text-[13px] font-semibold rounded-full px-4 py-2 transition-all hover:scale-[1.02] shadow-[0_3px_10px_rgba(107,20,20,0.22)]"
            >
              <Phone className="h-3.5 w-3.5" />
              <span className="ml-0.5 whitespace-nowrap">Get Help Now 856-200-3127</span>
            </a>
          </div>

          {/* Mobile triggers */}
          <button
            onClick={() => setOpen(!open)}
            className="min-[1180px]:hidden inline-flex items-center justify-center h-11 w-11 text-burgundy"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile full-screen overlay */}
      {open && (
        <div className="min-[1180px]:hidden fixed inset-0 top-0 bg-white z-50 animate-fade-in flex flex-col">
          <div className="flex items-center justify-between h-[72px] px-6 border-b border-warm-border">
            <img src={logo} alt="Cherry Hill Recovery Center" width={102} height={26} className="h-[26px] w-auto" />
            <button onClick={() => setOpen(false)} className="text-burgundy" aria-label="Close menu">
              <X size={28} />
            </button>
          </div>
          <a
            href="tel:8562003127"
            className="bg-burgundy text-cream-warm font-bold text-[18px] py-5 flex items-center justify-center gap-2"
          >
            <Phone className="h-5 w-5" />
            Call Now — 856-200-3127
          </a>
          <div className="flex-1 overflow-y-auto">
            <MobileAccordion title="Treatment Programs">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-burgundy/70 px-2 pt-2">Outpatient Programs</div>
              {outpatientPrograms.map((i) => <MobileSubLink key={i.label} href={i.href} onClick={() => setOpen(false)}>{i.label}</MobileSubLink>)}
              <div className="text-[11px] font-semibold uppercase tracking-wider text-burgundy/70 px-2 pt-3">Additional Services</div>
              {additionalServices.map((i) => <MobileSubLink key={i.label} href={i.href} badge={i.badge} onClick={() => setOpen(false)}>{i.label}</MobileSubLink>)}
            </MobileAccordion>
            <MobileAccordion title="Conditions We Treat">
              {conditions.map((i) => <MobileSubLink key={i.label} href={i.href} onClick={() => setOpen(false)}>{i.label}</MobileSubLink>)}
            </MobileAccordion>
            <MobileLink href="/verify-insurance" onClick={() => setOpen(false)}>
              <span style={{ color: "#C9A84C", marginRight: 6 }}>⚡</span>Insurance
            </MobileLink>
            <MobileAccordion title="About">
              {aboutItems.map((i) => <MobileSubLink key={i.label} href={i.href} onClick={() => setOpen(false)}>{i.label}</MobileSubLink>)}
            </MobileAccordion>
            <MobileLink href="/addiction-resources-new-jersey" onClick={() => setOpen(false)}>Resources</MobileLink>
            <MobileLink href="/contact" onClick={() => setOpen(false)}>Contact</MobileLink>
          </div>
        </div>
      )}
    </div>
  );
};

const DropLink = ({ href, children, badge }: { href: string; children: React.ReactNode; badge?: string | undefined }) => (
  <a
    href={href}
    className="flex items-center text-[14px] text-body-dark hover:bg-cream-warm hover:text-burgundy transition-colors"
    style={{ padding: "10px 20px" }}
  >
    <span>{children}</span>
    {badge && (
      <span
        style={{
          marginLeft: 6,
          background: "rgba(139,26,26,0.1)",
          color: "#8B1A1A",
          fontFamily: "Helvetica, Arial, sans-serif",
          fontWeight: 700,
          fontSize: 10,
          borderRadius: 50,
          padding: "2px 8px",
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 1.2,
          letterSpacing: "0.04em",
        }}
      >
        {badge}
      </span>
    )}
  </a>
);

const MobileLink = ({ href, onClick, children }: { href: string; onClick: () => void; children: React.ReactNode }) => (
  <a
    href={href}
    onClick={onClick}
    className="block text-[18px] text-body-dark px-6 py-4 border-b border-warm-border"
  >
    {children}
  </a>
);

const MobileSubLink = ({ href = "#programs", onClick, children, badge }: { href?: string; onClick: () => void; children: React.ReactNode; badge?: string | undefined }) => (
  <a
    href={href}
    onClick={onClick}
    className="flex items-center text-[16px] text-body-dark px-2 py-2"
  >
    <span>{children}</span>
    {badge && (
      <span
        style={{
          marginLeft: 6,
          background: "rgba(139,26,26,0.1)",
          color: "#8B1A1A",
          fontFamily: "Helvetica, Arial, sans-serif",
          fontWeight: 700,
          fontSize: 10,
          borderRadius: 50,
          padding: "2px 8px",
          lineHeight: 1.2,
        }}
      >
        {badge}
      </span>
    )}
  </a>
);

const MobileAccordion = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-b border-warm-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-[18px] text-body-dark px-6 py-4"
      >
        {title}
        <ChevronDown className={`h-5 w-5 text-burgundy transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="bg-cream-warm px-6 py-3">
          {children}
        </div>
      )}
    </div>
  );
};

export default Header;
